import React, { useEffect, useState } from 'react';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { audioEngine } from '../services/audioEngine';
import { api } from '../services/api';
import { SLICE_DISTRICT } from '../config';
import { TOWN_LOCATIONS } from '../rooms';
import { useWorldTime } from '../worldTime';

const SHORT = { studio: 'Studio', 'listening-room': 'Listening', 'record-store': 'Records', 'concert-hall': 'Concert', theater: 'Cinema' };
const MODEL_MODE = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('city') === 'model';

// The minimap AUTO-FITS to whatever it's showing (the venues + any present
// follows), so nothing clamps to a corner. Build a world(x,z) → viewBox(100×80)
// projection from a set of world points: fit their bounding box into an inset
// frame, preserving relative positions. The venue block alone reproduces the
// classic spread; a far-flung follower just zooms the frame out to include it.
function makeProject(worldPts) {
  const PAD_X = 12, PAD_Y = 12, W = 100, H = 80;         // viewBox inset
  const xs = worldPts.map((p) => p[0]); const zs = worldPts.map((p) => p[1]);
  let minX = Math.min(...xs), maxX = Math.max(...xs);
  let minZ = Math.min(...zs), maxZ = Math.max(...zs);
  // Guard degenerate spans (single point / collinear) so we never divide by 0.
  if (maxX - minX < 1) { minX -= 20; maxX += 20; }
  if (maxZ - minZ < 1) { minZ -= 20; maxZ += 20; }
  // Z is inverted (1 − …) so NORTH is UP: larger world z (deeper into the block)
  // sits toward the top, matching the venues' original map orientation.
  return (x, z) => [
    PAD_X + ((x - minX) / (maxX - minX)) * (W - 2 * PAD_X),
    PAD_Y + (1 - (z - minZ) / (maxZ - minZ)) * (H - 2 * PAD_Y),
  ];
}

// Track viewport width so the chrome can reflow for phones (inline styles don't
// respond to media queries on their own).
function useViewport() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

// TownMap — a clickable HUD minimap of the block. A dot per location, placed
// at its layout coord. Click travels the camera to that spot; clicking the
// venue you're already at steps inside. Highlights where you are now.
function TownMap({ narrow = false }) {
  const streetFocus = useWorldStore((s) => s.streetFocus);
  const setStreetFocus = useWorldStore((s) => s.setStreetFocus);
  const setPendingEnter = useWorldStore((s) => s.setPendingEnter);
  const setWalkTarget = useWorldStore((s) => s.setWalkTarget);
  const venueAnchors = useWorldStore((s) => s.venueAnchors);
  const interior = useWorldStore((s) => s.interior);
  const npcList = useWorldStore((s) => s.npcList);
  const following = useWorldStore((s) => s.following);
  const owned = useWorldStore((s) => s.owned);
  const showPeople = useWorldStore((s) => s.showPeople);
  const toggleShowPeople = useWorldStore((s) => s.toggleShowPeople);
  const [open, setOpen] = useState(!narrow);
  const active = interior || streetFocus;

  // Residents you OWN or FOLLOW that are present here → headshot pins on the map
  // (mine ∪ following ∩ roster; never thousands). `mine` wins for the ring color.
  // Tapping one fast-travels to them. Hidden when the people toggle is off (a lot
  // of owned avatars otherwise cloud the map).
  const allMarks = npcList
    .filter((n) => n.avatarId != null && Array.isArray(n.post) && (owned.includes(n.avatarId) || following.includes(n.avatarId)))
    .map((n) => ({ ...n, mine: owned.includes(n.avatarId) }));
  const followMarks = showPeople ? allMarks : [];
  // Auto-fit projection over the venues + any present follows, so a far-flung
  // follower zooms the frame out instead of clamping to a corner.
  const project = React.useMemo(
    () => makeProject([
      ...TOWN_LOCATIONS.filter((l) => Array.isArray(l.world)).map((l) => l.world),
      ...followMarks.map((n) => [n.post[0], n.post[2]]),
    ]),
    [followMarks.map((n) => n.avatarId).join(',')],
  );
  const goToResident = (n) => {
    const [x, , z] = n.post;
    // Smooth flyover to a spot beside them, facing them (walkTarget = the same
    // quick-travel the venue dots use; y is overridden to eye level downstream).
    setWalkTarget({ pos: [x + 3.5, 0, z + 3.5], look: [x, 0, z] });
  };

  const onPick = (loc) => {
    // In the walkable model city: quick-travel (teleport) to the venue's
    // building; the beacon there steps you inside.
    if (MODEL_MODE && venueAnchors && venueAnchors[loc.key]) {
      const a = venueAnchors[loc.key];
      setStreetFocus(loc.key);
      setWalkTarget({ pos: [a[0] + 6, 1.7, a[2] + 6], look: [a[0], 1.5, a[2]], enter: loc.interiorId });
      return;
    }
    // Elsewhere: fly across town to the venue, then auto-enter on arrival.
    if (loc.interiorId) { setPendingEnter(loc.key); setStreetFocus(loc.key); }
    else setStreetFocus(loc.key);
  };

  // Collapsed on phones: a small chip that expands the map on tap.
  if (narrow && !open) {
    return (
      <button onClick={() => setOpen(true)} style={{
        position: 'absolute', right: 'max(12px, env(safe-area-inset-right))', top: 'max(12px, env(safe-area-inset-top))',
        pointerEvents: 'auto', background: 'rgba(16,10,6,0.72)', backdropFilter: 'blur(6px)',
        border: '1px solid rgba(255,178,94,0.35)', borderRadius: 999, color: '#ffe9c4',
        padding: '8px 13px', fontSize: 12.5, fontFamily: 'system-ui, sans-serif',
      }}>🗺 Map</button>
    );
  }
  const svgW = narrow ? 132 : 166, svgH = narrow ? 106 : 133;
  return (
    <div style={{
      position: 'absolute', right: narrow ? 'max(12px, env(safe-area-inset-right))' : 16, top: narrow ? 'max(12px, env(safe-area-inset-top))' : 16,
      width: narrow ? 156 : 190, pointerEvents: 'auto',
      background: 'rgba(16,10,6,0.72)', backdropFilter: 'blur(6px)',
      border: '1px solid rgba(255,178,94,0.35)', borderRadius: 12, padding: '10px 12px 8px',
      color: '#ffe9c4', fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 10.5, letterSpacing: 1.4, textTransform: 'uppercase', opacity: 0.75 }}>Hip-Hop Town · map</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* People toggle — hide the owned/followed avatar pins when they crowd
              the map. Highlighted when ON; dim when OFF. */}
          <button
            onClick={toggleShowPeople}
            title={showPeople ? 'Hide people on map' : 'Show people on map'}
            aria-pressed={showPeople}
            style={{
              display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', padding: '2px 6px',
              borderRadius: 999, fontSize: 11, lineHeight: 1, fontFamily: 'system-ui, sans-serif',
              border: `1px solid ${showPeople ? 'rgba(255,210,125,0.6)' : 'rgba(255,255,255,0.18)'}`,
              background: showPeople ? 'rgba(255,210,125,0.16)' : 'transparent',
              color: showPeople ? '#ffd27d' : 'rgba(255,233,196,0.5)',
            }}
          >
            <span style={{ fontSize: 12, opacity: showPeople ? 1 : 0.55 }}>{showPeople ? '👥' : '🚫'}</span>
            {allMarks.length > 0 && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{allMarks.length}</span>}
          </button>
          {narrow && <span onClick={() => setOpen(false)} style={{ cursor: 'pointer', opacity: 0.7, padding: '0 2px', fontSize: 13 }}>✕</span>}
        </div>
      </div>
      <svg viewBox="0 0 100 80" width={svgW} height={svgH} style={{ display: 'block' }}>
        {/* faint block footprint (the map auto-fits, so no fixed street/alley
            decor — it wouldn't line up once the frame zooms to the follows) */}
        <rect x="2" y="2" width="96" height="76" rx="4" fill="rgba(255,255,255,0.03)" stroke="rgba(255,178,94,0.18)" strokeWidth="0.6" />
        {TOWN_LOCATIONS.map((loc) => {
          const [cx, cy] = Array.isArray(loc.world) ? project(loc.world[0], loc.world[1]) : [loc.map[0] * 100, loc.map[1] * 80];
          const on = active === loc.key;
          return (
            <g key={loc.key} onClick={() => onPick(loc)} style={{ cursor: 'pointer' }}>
              {on && <circle cx={cx} cy={cy} r="6.5" fill="none" stroke={loc.accent} strokeWidth="1.2" opacity="0.9" />}
              <circle cx={cx} cy={cy} r="3.6" fill={loc.accent} stroke="#1a0f00" strokeWidth="0.6" />
              <text x={cx} y={cy - 5.5} fill={on ? '#fff' : '#ffe9c4'} fontSize="5.2" textAnchor="middle" style={{ fontWeight: on ? 700 : 500 }}>
                {SHORT[loc.key] || loc.name}
              </text>
            </g>
          );
        })}
        {/* Followed residents present in the district — headshot pins with a
            gold ring; tap to fast-travel to them. */}
        {followMarks.map((n) => {
          const [cx, cy] = project(n.post[0], n.post[2]);
          const clip = `fp${n.avatarId}`;
          const first = (n.name || '').split(' ')[0];
          const ring = n.mine ? '#4fd1ff' : '#ffd27d'; // mine = teal · following = gold
          return (
            <g key={clip} onClick={() => goToResident(n)} style={{ cursor: 'pointer' }}>
              <defs><clipPath id={clip}><circle cx={cx} cy={cy} r="4.4" /></clipPath></defs>
              {n.headshotUrl
                ? <image href={n.headshotUrl} x={cx - 4.4} y={cy - 4.4} width="8.8" height="8.8" clipPath={`url(#${clip})`} preserveAspectRatio="xMidYMid slice" />
                : <circle cx={cx} cy={cy} r="4.4" fill={ring} />}
              <circle cx={cx} cy={cy} r="4.4" fill="none" stroke={ring} strokeWidth="1.1" />
              <circle cx={cx} cy={cy} r="5.8" fill="none" stroke={ring} strokeWidth="0.5" opacity="0.5" />
              <text x={cx} y={cy + 9} fill="#ffe9c4" fontSize="4.6" textAnchor="middle" style={{ fontWeight: 700 }}>{first}</text>
            </g>
          );
        })}
      </svg>
      <div style={{ fontSize: 10.5, opacity: 0.7, marginTop: 4, lineHeight: 1.35 }}>
        {interior
          ? <>Inside <b>{SHORT[interior]}</b> · tap another to switch</>
          : followMarks.length
            ? <>Tap to enter a venue · <b style={{ color: '#4fd1ff' }}>◉</b> mine · <b style={{ color: '#ffd27d' }}>◉</b> following — tap a face to travel</>
            : 'Tap a location to go inside'}
      </div>
    </div>
  );
}

// HUD — minimal chrome. Entry gate, navigation (up/back), broadcast/pulse
// toggle, now-playing ticker, and a status line so it's never ambiguous.
export default function Hud() {
  const zoom = useWorldStore((s) => s.zoom);
  const setZoom = useWorldStore((s) => s.setZoom);
  const interior = useWorldStore((s) => s.interior);
  const exitInterior = useWorldStore((s) => s.exitInterior);
  const musicOn = useWorldStore((s) => s.musicOn);
  const toggleMusic = useWorldStore((s) => s.toggleMusic);
  const nowPlaying = useWorldStore((s) => s.nowPlaying);
  const setModulation = useWorldStore((s) => s.setModulation);

  const [status, setStatus] = useState('idle'); // idle | starting | live | pulse | offline
  const [entered, setEntered] = useState(false);

  // The town's local clock (Oakland) — re-evaluated with the world-time hook,
  // so a ?time override shows its pinned phase while the clock shows reality.
  const tod = useWorldTime(SLICE_DISTRICT.timezone);
  const clockText = new Intl.DateTimeFormat('en-US', {
    timeZone: SLICE_DISTRICT.timezone, hour: 'numeric', minute: '2-digit'
  }).format(new Date());

  // Stop audio on unmount.
  useEffect(() => () => audioEngine.stop(), []);

  // Pull the modulation track for whatever is currently playing.
  useEffect(() => {
    const cur = nowPlaying?.current;
    if (nowPlaying?.playing && cur?.item_type === 'track' && cur?.track_id) {
      api.worldModulation(cur.track_id)
        .then((m) => setModulation(m?.modulation || m))
        .catch(() => setModulation(null));
    }
  }, [nowPlaying?.current?.track_id]);

  // Reflect audio state in the status line.
  useEffect(() => {
    if (!entered) return;
    if (!musicOn) { setStatus('pulse'); return; }
    setStatus(nowPlaying?.playing ? 'live' : 'offline');
  }, [entered, musicOn, nowPlaying?.playing]);

  const enter = async () => {
    setStatus('starting');
    setEntered(true);
    // Start the broadcast layer ON the user gesture (autoplay policy). If the
    // station has nothing ready, fall back to the ambient pulse so there's
    // always sound.
    try {
      await audioEngine.setMusicOn(true);
    } catch {
      await audioEngine.setMusicOn(false);
    }
    // Straight to street — CameraRig flies the cinematic drone descent (a
    // banking orbit around the city while it drops + zooms in) all the way down.
    setZoom(ZOOM.STREET);
  };

  const onToggleMusic = async () => {
    const next = !musicOn;
    toggleMusic();
    // In the concert hall / cinema the "music" is the on-screen video's audio —
    // the PerformerVideo / TheaterScreen read musicOn to mute/unmute; don't drive
    // the district station here (it stays ducked under the venue).
    if (interior === 'concert-hall' || interior === 'theater') return;
    try { await audioEngine.setMusicOn(next); } catch { /* stay silent */ }
  };

  const goUp = () => {
    if (zoom === ZOOM.INTERIOR) { exitInterior(); return; }
    if (zoom === ZOOM.STREET) { setZoom(ZOOM.DISTRICT); return; }
    if (zoom === ZOOM.DISTRICT) { setZoom(ZOOM.ORBIT); return; }
  };
  const goDown = () => {
    if (zoom === ZOOM.ORBIT) { setZoom(ZOOM.DISTRICT); return; }
    if (zoom === ZOOM.DISTRICT) { setZoom(ZOOM.STREET); return; }
  };

  const cur = nowPlaying?.current;
  const ticker = nowPlaying?.playing && cur
    ? (cur.item_type === 'track'
        ? `${cur.artist || cur.avatar_name || 'HypeTunes'} — ${cur.title || ''}`
        : `${SLICE_DISTRICT.name} DJ`)
    : `${SLICE_DISTRICT.name}`;

  const statusText = {
    idle: '', starting: 'Connecting to the broadcast…',
    live: 'Broadcast — live', pulse: 'Pulse — ambient soundscape',
    offline: 'Station quiet — ambient playing'
  }[status];

  const btn = {
    background: 'rgba(20,12,6,0.65)', color: '#ffd27d',
    border: '1px solid rgba(255,178,94,0.35)', borderRadius: 999,
    padding: '10px 16px', cursor: 'pointer', fontSize: 14, pointerEvents: 'auto'
  };

  const vw = useViewport();
  const narrow = vw < 640;
  const IS_TOUCH = typeof window !== 'undefined' && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);
  const navBtn = { ...btn, padding: narrow ? '8px 12px' : '10px 16px', fontSize: narrow ? 12.5 : 14 };
  const upLabel = zoom === ZOOM.INTERIOR ? (narrow ? 'Back' : 'Back to street')
    : zoom === ZOOM.STREET ? (narrow ? 'District' : 'Up to district')
    : (narrow ? 'Orbit' : 'Up to orbit');
  // Fullscreen toggle — works on Android/desktop; iOS Safari has no element
  // fullscreen (use Add to Home Screen for a standalone fullscreen app).
  const toggleFullscreen = () => {
    const el = document.documentElement;
    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
      else (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } catch { /* unsupported */ }
  };
  const fsSupported = typeof document !== 'undefined' && (document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen);
  // iOS has no element Fullscreen API, so the real fullscreen path is Add to
  // Home Screen (launches the World standalone, no browser chrome). Show a prompt
  // in place of the (impossible) fullscreen button — unless already standalone.
  const isStandalone = typeof window !== 'undefined'
    && (window.navigator.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches);
  const iosA2HS = !fsSupported && IS_TOUCH && !isStandalone;
  const [showA2HS, setShowA2HS] = useState(false);
  // Keep top/bottom chrome inside the notch/home-indicator safe areas.
  const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
      {/* Entry gate */}
      {!entered && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'flex-end', paddingBottom: '12vh', gap: 14,
          pointerEvents: 'auto', background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(10,4,0,0.45) 100%)'
        }}>
          <div style={{ color: '#ffe9c4', textAlign: 'center' }}>
            <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: 0.5, textShadow: '0 4px 24px rgba(0,0,0,0.6)' }}>
              Planet HypeTunes
            </div>
            <div style={{ fontSize: 15, opacity: 0.85, marginTop: 6 }}>
              {IS_TOUCH ? 'Drag to look · joystick to move · rotate for wide view' : 'Drag to look around · scroll to zoom'}
            </div>
          </div>
          <button onClick={enter} style={{
            background: 'linear-gradient(180deg,#ffb25e,#ff5e78)', color: '#1a0f00',
            border: 'none', padding: '18px 38px', borderRadius: 999, fontSize: 20,
            fontWeight: 800, cursor: 'pointer', boxShadow: '0 10px 40px rgba(255,94,120,0.45)'
          }}>
            ▶ Enter {SLICE_DISTRICT.name}
          </button>
          {status === 'starting' && (
            <div style={{ color: '#ffd27d', fontSize: 13, opacity: 0.85 }}>{statusText}</div>
          )}
        </div>
      )}

      {/* In-world chrome */}
      {entered && (
        <>
          {/* Top-left: navigation + fullscreen */}
          <div style={{ position: 'absolute', left: safe('left', 16), top: safe('top', 16), display: 'flex', gap: 8, alignItems: 'center' }}>
            {zoom !== ZOOM.ORBIT && (
              <button onClick={goUp} style={navBtn}>← {upLabel}</button>
            )}
            {(zoom === ZOOM.ORBIT || zoom === ZOOM.DISTRICT) && (
              <button onClick={goDown} style={navBtn}>
                {zoom === ZOOM.ORBIT ? (narrow ? 'District ↓' : 'Down to district ↓') : (narrow ? 'Street ↓' : 'Down to street ↓')}
              </button>
            )}
            {fsSupported && (
              <button onClick={toggleFullscreen} title="Fullscreen" style={{ ...navBtn, padding: narrow ? '8px 11px' : '10px 13px' }}>⛶</button>
            )}
            {iosA2HS && (
              <button onClick={() => setShowA2HS(true)} title="Fullscreen" style={{ ...navBtn, padding: narrow ? '8px 11px' : '10px 13px' }}>⛶</button>
            )}
          </div>

          {/* Top-center: the town clock — the district lives on its city's real time.
              Short on phones so it can't collide with the nav / map. */}
          {!(narrow && zoom === ZOOM.INTERIOR) && (
            <div style={{
              position: 'absolute', left: '50%', top: safe('top', 16), transform: 'translateX(-50%)',
              color: '#ffe9c4', background: 'rgba(20,12,6,0.55)',
              border: '1px solid rgba(255,178,94,0.3)', borderRadius: 999,
              padding: narrow ? '6px 11px' : '7px 16px', fontSize: narrow ? 11 : 12.5,
              letterSpacing: 1.2, textTransform: 'uppercase', whiteSpace: 'nowrap',
            }}>
              {narrow ? clockText : `${SLICE_DISTRICT.city} · ${clockText} · ${tod.phase}`}
            </div>
          )}

          {/* Top-right: the clickable town map (street + interior) */}
          {(zoom === ZOOM.STREET || zoom === ZOOM.INTERIOR) && <TownMap narrow={narrow} />}

          {/* Now playing + status. On touch it sits above the audio toggle (bottom
              -right) so the movement joystick (bottom-left) never overlaps it. */}
          <div style={{
            position: 'absolute', pointerEvents: 'auto',
            ...(IS_TOUCH
              ? { right: safe('right', 16), bottom: `calc(${safe('bottom', 16)} + 52px)`, maxWidth: '58vw' }
              : { left: safe('left', 16), bottom: safe('bottom', 16), maxWidth: 340 }),
            background: 'rgba(20,12,6,0.6)', backdropFilter: 'blur(6px)',
            border: '1px solid rgba(255,178,94,0.35)', borderRadius: 12,
            color: '#ffd27d', padding: narrow ? '8px 12px' : '10px 14px',
          }}>
            <div style={{ fontSize: narrow ? 10 : 11, opacity: 0.7, letterSpacing: 1, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {statusText}
            </div>
            <div style={{ fontSize: narrow ? 13 : 14, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {ticker}
            </div>
          </div>

          {/* Bottom-right: audio toggle */}
          <button onClick={onToggleMusic} style={{ ...navBtn, position: 'absolute', right: safe('right', 16), bottom: safe('bottom', 16) }}>
            {musicOn ? '⏸ Music' : '▶ Music'}
          </button>

          {/* iOS "Add to Home Screen for fullscreen" prompt (no element
              Fullscreen API on iOS — this is the real path). */}
          {showA2HS && (
            <div
              onClick={() => setShowA2HS(false)}
              style={{
                position: 'absolute', inset: 0, zIndex: 30, pointerEvents: 'auto',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
                background: 'radial-gradient(ellipse at center, rgba(6,3,0,0.5) 30%, rgba(6,3,0,0.75) 100%)',
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: 330, width: '100%', background: 'rgba(20,12,6,0.96)',
                  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 16, padding: '20px 20px 16px',
                  color: '#ffe9c4', fontFamily: 'system-ui, sans-serif', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                }}
              >
                <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 6 }}>Play fullscreen</div>
                <div style={{ fontSize: 13.5, opacity: 0.85, lineHeight: 1.5, marginBottom: 14 }}>
                  iPhone &amp; iPad don&apos;t allow in-page fullscreen. Add Planet HypeTunes to your Home Screen and it opens fullscreen — no browser bars.
                </div>
                <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    <>Tap the <b>Share</b> button
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: '-2px', margin: '0 3px' }}>
                        <path d="M12 15V3m0 0L8 7m4-4l4 4" stroke="#ffd27d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M6 12H5a2 2 0 00-2 2v5a2 2 0 002 2h14a2 2 0 002-2v-5a2 2 0 00-2-2h-1" stroke="#ffd27d" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      in the toolbar</>,
                    <>Choose <b>Add to Home Screen</b></>,
                    <>Open it from your Home Screen</>,
                  ].map((step, i) => (
                    <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13.5, lineHeight: 1.45 }}>
                      <span style={{
                        flexShrink: 0, width: 20, height: 20, borderRadius: 999, background: '#ffb25e', color: '#1c1206',
                        fontSize: 12, fontWeight: 800, display: 'grid', placeItems: 'center',
                      }}>{i + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                <button
                  onClick={() => setShowA2HS(false)}
                  style={{
                    marginTop: 16, width: '100%', background: 'rgba(255,178,94,0.16)', color: '#ffd27d',
                    border: '1px solid rgba(255,178,94,0.4)', borderRadius: 10, padding: '10px 0',
                    cursor: 'pointer', font: '600 14px system-ui',
                  }}
                >
                  Got it
                </button>
              </div>
            </div>
          )}

          <PickedReadout />
        </>
      )}
    </div>
  );
}

// ?pick=1 debug readout — shows the last clicked mesh so we can target a sign.
function PickedReadout() {
  const picked = useWorldStore((s) => s.picked);
  if (!picked) return null;
  return (
    <div style={{
      position: 'absolute', left: 16, bottom: 84, pointerEvents: 'auto',
      background: 'rgba(6,10,20,0.85)', border: '1px solid #4fd1ff', borderRadius: 10,
      color: '#cdefff', padding: '10px 14px', font: '12px ui-monospace, monospace', maxWidth: 380,
    }}>
      <div style={{ opacity: 0.7, marginBottom: 4 }}>PICKED MESH</div>
      <div>mesh: <b>{picked.name}</b></div>
      <div>material: <b>{picked.material}</b></div>
      <div>size: {picked.size.join(' × ')} m</div>
    </div>
  );
}